import React from 'react';
import {
  Button,
  FlexRow,
  Header3Text,
  Icon,
  IconSize,
  IconType,
  ISpruceFieldProps,
  RadioList,
  SpruceField,
} from '../../../ui-toolkit';
import { EntityForm } from './EntityForm';
import { IWhoHasFocus } from './IWhoHasFocus';
import { PersonForm } from './PersonForm';
import styled from 'styled-components';
import { Field, FieldProps } from 'formik';
import { ePartyType } from '../../../ui-toolkit/enums/ePartyType';
import { DisplayPartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { DisplayPartyType } from '../../../ui-toolkit/enums/ePartyTypeData';
import { mergeTests } from '../../../ui-toolkit/test-utilities/mergeTests';

interface IPartyInformationFormProps {
  /** Identifier string for formik */
  index: number;
  displayIndex: number;
  onClickDelete: () => void;
  showDelete: boolean;
  focus: IWhoHasFocus;
  setFocus: (x: IWhoHasFocus) => void;
  title: string;
}

const Header = styled(Header3Text)`
  white-space: nowrap;
`;

export const PartyInformationForm: React.FC<IPartyInformationFormProps> = ({
  index,
  displayIndex,
  onClickDelete,
  showDelete,
  focus,
  setFocus,
}) => {
  const fieldNameStart = `parties.${index}`;
  const onPartyTypeChange = ({ field, form }: FieldProps<string, any>) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      field.onChange(event);
      if (event.target.value === ePartyType.person) {
        form.setFieldValue(`${fieldNameStart}.signatories` as any, []);
      }
      setFocus({ doesAnyone: true, partyIndex: index, signatoryIndex: -1 });
    };
  };
  const typeOptions = [
    { label: DisplayPartyType[ePartyType.person], value: ePartyType.person },
    { label: DisplayPartyType[ePartyType.entity], value: ePartyType.entity },
  ];
  return (
    <SpruceField name={`parties[${index}]`}>
      {({ field }: ISpruceFieldProps) => (
        <>
          <FlexRow verticalAlign={'baseline'}>
            <Header data-test-id={mergeTests('party', 'header', `${index}`)}>
              {DisplayPartyPurpose[field.value.purpose]} {displayIndex + 1}
            </Header>
            <Field name={`${fieldNameStart}.type`}>
              {(props: FieldProps<string, any>) => (
                <RadioList
                  {...props.field}
                  data-test-id={field.name}
                  onChange={onPartyTypeChange(props)}
                  options={typeOptions}
                  orientation={'horizontal'}
                />
              )}
            </Field>
            <div className={'min-h-3 w-full border-t border-neutral-200'} />
            {showDelete && (
              <Button
                type={'button'}
                intent="ghost"
                isInline
                onClick={onClickDelete}
                data-test-id={mergeTests('party', `${index}`, `delete`)}
              >
                <Icon type={IconType.Remove} size={IconSize.Small} />
              </Button>
            )}
          </FlexRow>

          {field.value.type === ePartyType.person ? (
            <PersonForm index={index} focus={focus} />
          ) : (
            <EntityForm index={index} focus={focus} setFocus={setFocus} />
          )}
        </>
      )}
    </SpruceField>
  );
};
